/**
 * Capability keys for the Workforce Explorer (/workforce-explorer), plus the
 * roles that already imply each one.
 *
 * These MIRROR the server guards exactly — the server is the control, this only
 * decides what to render. Keep them in step with the backend:
 *   whole dashboard : RequirePermission(WORKFORCE_KEYS, ORG_CONSOLE_ROLES)
 *   export.xlsx     : RequirePermission("workforce:export", ORG_CONSOLE_ROLES)
 * both keys registered in backend/shared/auth/permission_registry.py — a key
 * that is not registered there cannot be granted at all.
 *
 * Getting these wrong is user-visible in both directions: too narrow and someone
 * who CAN read the dashboard never finds the way in; too wide and they get a nav
 * item and a download button that 403 on click.
 */

/** Roles that imply the whole dashboard — mirrors backend WORKFORCE_CONSOLE_ROLES
 *  (shared/auth/authorization.py). NARROWER than ORG_CONSOLE_ROLES on purpose:
 *  this page is establishment-wide workforce reporting (equity by race and grade
 *  band, turnover, single-incumbent risk, performance indicators), so access is a
 *  decision about a named person rather than a side effect of holding an
 *  operational console role. hrbp, fno_admin and data_team are NOT included —
 *  anyone who needs the page is granted workforce:explorer individually.
 *
 *  ⚠️ Must stay character-for-character in step with the backend list. Widening
 *  it here only produces a nav item and a download button that 403 on click. */
export const WORKFORCE_ROLES = ['admin', 'hr_admin'] as const

/**
 * The export key is deliberately NOT prefixed 'export:'. EXPORT_PERMISSION_KEYS
 * is derived from that prefix and drives the /admin sidebar branch, the Data
 * Export tab and integration-service's F&O export gate — naming this key
 * 'export:workforce-explorer' would hand a workforce-only grantee incidental
 * HR Admin visibility they were never meant to have.
 */
export const WORKFORCE_KEYS = {
  view: 'workforce:explorer',
  export: 'workforce:export',
} as const

export const WORKFORCE_PERMISSION_KEYS = Object.values(WORKFORCE_KEYS)

/**
 * Roles that may trigger an on-demand frame rebuild — mirrors integration-service's
 * _REBUILD_GATE in routers/workforce_frames_admin.py.
 *
 * WIDER than WORKFORCE_ROLES (it includes fno_admin, who owns the F&O sync this
 * rebuild reads) and NOT implied by any workforce:* grant: reading the dashboard
 * is not authority to republish what it serves. There is no permission key for
 * it, deliberately — a rebuild rewrites the numbers on a board dashboard, and
 * that stays a role decision rather than something granted per user.
 *
 * ⚠️ Must stay in step with the backend list; widening it here only produces a
 * button that 403s.
 */
export const WORKFORCE_SYNC_ROLES = ['admin', 'hr_admin', 'fno_admin'] as const
